import { hydrate, type DehydratedState, type QueryClient } from '@tanstack/react-query'
import { ANALYTICS_GC_TIME_MS, isAnalyticsQueryKey } from './analytics-query'

type DehydratedQuery = DehydratedState['queries'][number]

// localStorage is listed as a forbidden pattern in state-management.md and frontend-ts.md. That
// rule is about MFE isolation; this is platform-host state whose key already carries the
// workspace via analyticsQueryKey, so a workspace switch is a different entry, never a stale
// read. Same deviation as lib/last-workspace-store.ts (ASC-10715).
// Ref: ASC-10749
// Bump when a persisted response shape changes incompatibly: older entries are then never read.
export const ANALYTICS_CACHE_VERSION = 1
const STORAGE_PREFIX = 'scorpio:analytics-cache:'

// A day-old number is still a better first paint than a skeleton: it renders at once and is
// revalidated behind the scenes because it is past its stale time.
export const ANALYTICS_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000

// localStorage gives the whole origin ~5M characters; stay well under so other features keep theirs.
export const ANALYTICS_CACHE_MAX_CHARS = 2_500_000

/** One slot per user and org, so one person's numbers are never read back as another's. */
export function analyticsCacheStorageKey(userId: string, orgId: string): string {
  return `${STORAGE_PREFIX}v${ANALYTICS_CACHE_VERSION}:${orgId}:${userId}`
}

export function isAnalyticsCacheStorageKey(key: string): boolean {
  return key.startsWith(STORAGE_PREFIX)
}

function isRestorable(query: DehydratedQuery, now: number): boolean {
  return (
    Array.isArray(query.queryKey) &&
    isAnalyticsQueryKey(query.queryKey) &&
    query.state?.status === 'success' &&
    query.state.data !== undefined &&
    typeof query.state.dataUpdatedAt === 'number' &&
    now - query.state.dataUpdatedAt < ANALYTICS_CACHE_MAX_AGE_MS
  )
}

/** Keeps the freshest entries that fit the budget; the oldest go first. */
export function trimToBudget(queries: DehydratedQuery[], maxChars: number): DehydratedQuery[] {
  const newestFirst = [...queries].sort(
    (a, b) => (b.state.dataUpdatedAt ?? 0) - (a.state.dataUpdatedAt ?? 0),
  )
  const kept: DehydratedQuery[] = []
  let used = 0
  for (const query of newestFirst) {
    const size = JSON.stringify(query).length
    if (used + size > maxChars) continue
    kept.push(query)
    used += size
  }
  return kept
}

export function serializeAnalyticsCache(state: DehydratedState, now: number): string {
  const queries = trimToBudget(
    state.queries.filter((query) => isRestorable(query, now)),
    ANALYTICS_CACHE_MAX_CHARS,
  )
  return JSON.stringify({ queries })
}

/** Anything unreadable, expired or foreign is dropped: a bad cache must never break the page. */
export function parseAnalyticsCache(raw: string | null, now: number): DehydratedState | null {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    const queries = (parsed as { queries?: unknown } | null)?.queries
    if (!Array.isArray(queries)) return null
    const restorable = (queries as DehydratedQuery[]).filter(
      (query) => query && isRestorable(query, now),
    )
    return restorable.length > 0 ? { mutations: [], queries: restorable } : null
  } catch {
    return null
  }
}

export function getStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    // Blocked or unavailable (private mode, disabled site data, SSR): run from memory only.
    return null
  }
}

/**
 * Drops every analytics slot (all users, all orgs). Called wherever a session ends, the same
 * three sites as `clearLastWorkspaces()`, so a signed-out browser keeps no cost, token or
 * trace figures. Ref: ASC-10749
 */
export function clearAnalyticsCache(storage: Storage | null = getStorage()): void {
  if (!storage) return
  try {
    for (let i = storage.length - 1; i >= 0; i -= 1) {
      const key = storage.key(i)
      if (key && isAnalyticsCacheStorageKey(key)) storage.removeItem(key)
    }
  } catch {
    // Nothing to clear if storage cannot be enumerated.
  }
}

/** Returns whether anything was restored. */
export function restoreCache(
  queryClient: QueryClient,
  storage: Storage | null,
  storageKey: string,
): boolean {
  if (!storage) return false
  try {
    // Another user or org signed in on this browser: their slot is not ours to keep.
    for (let i = storage.length - 1; i >= 0; i -= 1) {
      const key = storage.key(i)
      if (key && key !== storageKey && isAnalyticsCacheStorageKey(key)) storage.removeItem(key)
    }
    const state = parseAnalyticsCache(storage.getItem(storageKey), Date.now())
    if (!state) return false
    // Without this, hydrated entries use the 5-minute default and fall out of memory early.
    hydrate(queryClient, state, { defaultOptions: { queries: { gcTime: ANALYTICS_GC_TIME_MS } } })
    return true
  } catch {
    // A cache that cannot be read is the same as no cache.
    return false
  }
}

/**
 * The slot moved to another user or org without a reload (a soft org switch). The in-memory
 * analytics entries belong to the previous slot and share keys with the new org's (the key
 * carries no org), so they are dropped before the next save could write them into the new slot.
 */
export function switchCacheSlot(
  queryClient: QueryClient,
  storage: Storage | null,
  storageKey: string | null,
): void {
  queryClient.removeQueries({ predicate: (query) => isAnalyticsQueryKey(query.queryKey) })
  if (storageKey) restoreCache(queryClient, storage, storageKey)
}
